<script setup lang="ts">
import type { Schemas } from '#shopware'

interface CartItemProps {
    item: Schemas['LineItem']
    showControls?: boolean
}

const props = withDefaults(defineProps<CartItemProps>(), {
    showControls: true
})

const emit = defineEmits<{
    'update:quantity': [quantity: number]
    'remove': []
}>()

const { getFormattedPrice } = usePrice()

// Get unit price for the cart item
const itemPrice = computed(() => {
    return props.item.price?.unitPrice || 0
})

const checkQuantity = computed (() => {
    return props.item.payload.stock || 0
})
// Update quantity or remove item if quantity reaches 0
const updateQuantity = (newQuantity: number) => {
    // Remove item when decreasing to 0 or below
    if (newQuantity <= 0) {
        emit('remove')
        return
    }
    // Otherwise update to new quantity
    emit('update:quantity', newQuantity)
}

// Emit remove event to parent component
const removeItem = () => {
    emit('remove')
}
</script>

<template>
    <div class="flex gap-4 items-center">
        <!-- Product image -->
        <div class="relative flex-shrink-0 w-20 h-20">
            <CartItemImage :product="item" />
        </div>

        <!-- Product details -->
        <div class="flex-1 min-w-0">
            <div class="flex justify-between h-full">
                <div class="flex flex-col justify-center">
                    <FoundationHeadline tag="h3" class="text-sm font-medium">
                        {{ item.label }}
                    </FoundationHeadline>

                    <!-- Product variant details if available -->
                    <div v-if="item.payload?.options?.length" class="mt-1">
                        <p v-for="(option, index) in item.payload.options" :key="index" class="text-xs">
                            {{ option.group }}: {{ option.option }}
                        </p>
                    </div>
                    
                    <div class="flex items-baseline gap-1 min-h-10">
                        <span v-if="item.price?.listPrice?.percentage" class="text-xs line-through">
                            {{ getFormattedPrice(item.price.listPrice.price) }}
                        </span>
                        <span class="text-sm font-bold">{{ getFormattedPrice(itemPrice) }}</span>
                    </div>
                </div>

                <div class="flex flex-col justify-between items-end gap-2 pl-2">
                    <!-- Quantity control -->
                    <div v-if="showControls" class="flex items-center border rounded">
                        <FoundationButton
                            aria-label="Decrease quantity"
                            size="small"
                            @click="updateQuantity(item.quantity - 1)"
                        >
                            <FoundationIcon name="minus" class="w-2 h-2" aria-hidden="true" />
                        </FoundationButton>
                        <span class="px-2 text-sm">{{ item.quantity }}</span>
                        <FoundationButton
                            aria-label="Increase quantity"
                            size="small"
                            :disabled="checkQuantity <= item.quantity"
                            @click="updateQuantity(item.quantity + 1)"
                        >
                            <FoundationIcon name="plus" class="w-2 h-2" aria-hidden="true" />
                        </FoundationButton>
                    </div>
                    <div v-else class="text-sm">
                        {{ $t('cart.quantity') }}: {{ item.quantity }}
                    </div>

                    <!-- Remove button -->
                    <div class="flex justify-end">
                        <FoundationButton
                            v-if="showControls"
                            class="text-base font-bold underline px-0"
                            size="small"
                            :aria-label="$t('cart.remove')"
                            @click="removeItem"
                        >
                            {{$t('cart.remove')}}
                        </FoundationButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
